import type { IssueStatus, ProposedFix } from "@/types/technicalAudit";
import type { IssueUiStatus } from "./StatusBadge";

type ReadabilityIssue = {
	issue_type?: string;
	fix_status?: IssueStatus | string;
	approved_value?: ProposedFix | string | null;
	proposed_fix?: ProposedFix | string | null;
	proposed_value?: unknown;
	error?: string | null;
	issue_details?: Record<string, unknown> | null;
};

/** An expert picked the ticket up (started, not yet resolved). Display-only:
 *  the row's real status stays `manual` — same lifecycle, same carry-forward —
 *  the badge just tells the client work is actively happening. */
function isExpertWorkInProgress(issue: ReadabilityIssue): boolean {
	if (issue.fix_status !== "manual") return false;
	const ticket = (issue.issue_details as Record<string, unknown> | null)?.[
		"manual_ticket"
	] as Record<string, unknown> | undefined;
	return Boolean(ticket?.started_at) && !ticket?.resolved_at;
}

function readFixObject(value: unknown): Record<string, unknown> | null {
	if (!value) return null;
	if (typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	if (!trimmed) return null;
	try {
		const parsed = JSON.parse(trimmed);
		return parsed && typeof parsed === "object" && !Array.isArray(parsed)
			? (parsed as Record<string, unknown>)
			: null;
	} catch {
		return null;
	}
}

export function readReadabilityFixObject(
	issue: ReadabilityIssue | null,
): Record<string, unknown> | null {
	if (!issue || issue.issue_type !== "low_readability") return null;
	return readFixObject(
		issue.approved_value || issue.proposed_fix || issue.proposed_value,
	);
}

export function hasReadabilitySuggestion(issue: ReadabilityIssue | null): boolean {
	const fix = readReadabilityFixObject(issue);
	if (!fix) return false;
	const sentenceDiffs = fix.sentence_diffs;
	if (Array.isArray(sentenceDiffs) && sentenceDiffs.length > 0) return true;
	return Boolean(fix.rewritten_body || fix.new_body || fix.content);
}

function hasAnySuggestion(issue: ReadabilityIssue | null): boolean {
	return Boolean(issue?.approved_value || issue?.proposed_fix || issue?.proposed_value);
}

export function isStaleReadabilitySentenceFix(
	issue: ReadabilityIssue | null,
): boolean {
	if (!issue || issue.issue_type !== "low_readability") return false;
	const fix = readReadabilityFixObject(issue);
	return Boolean(
		fix?.rewritten_body &&
			(!Array.isArray(fix.sentence_diffs) || fix.sentence_diffs.length === 0),
	);
}

export function getReadabilityDisplayStatus(
	issue: ReadabilityIssue,
): IssueUiStatus {
	// Any issue type: an in-progress expert ticket outranks the raw status for
	// display, so the client sees "Being fixed by expert" instead of a static
	// "Expert fix requested" while the work is actually happening.
	if (isExpertWorkInProgress(issue)) {
		return "manual_in_progress";
	}
	if (issue.issue_type !== "low_readability") {
		if (issue.fix_status === "pending" && hasAnySuggestion(issue)) {
			return "ready";
		}
		return (issue.fix_status as IssueUiStatus) || "pending";
	}

	if (
		issue.fix_status === "applied" ||
		issue.fix_status === "verified" ||
		issue.fix_status === "rejected" ||
		issue.fix_status === "skipped" ||
		// Terminal resolution: source page 404'd after the crawl. Must resolve to
		// the calm "obsolete" badge BEFORE the error-based needs_attention branch,
		// since obsolete carries an error message but is a resolution, not a fault.
		issue.fix_status === "obsolete"
	) {
		return issue.fix_status;
	}
	if (
		issue.fix_status === "failed" ||
		Boolean(issue.error) ||
		isStaleReadabilitySentenceFix(issue)
	) {
		return "needs_attention";
	}
	if (hasReadabilitySuggestion(issue) || issue.fix_status === "approved") {
		return "ready";
	}
	return "pending";
}

export function isReadabilityReadyForDirectApply(
	issue: ReadabilityIssue,
): boolean {
	return (
		issue.issue_type === "low_readability" &&
		getReadabilityDisplayStatus(issue) === "ready"
	);
}
